/**
 * TriageProjectPipeline Component - Interactive timeline visualization for triage projects - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * Displays triage projects as a timeline with drag-to-scroll functionality, interactive canvas
 * lines connecting start/end dates, and modal details. Features quarter-based timeline navigation
 * with visual indicators for project start/end dates.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of triage project data objects
 * @param {boolean} props.isActive - Whether this component is currently active/visible
 * @param {string} props.selectedYear - Currently selected year filter ('All' or specific year)
 * @param {string} props.selectedQuarter - Currently selected quarter filter (format: 'Q1/23')
 * @returns {JSX.Element} The TriageProjectPipeline component
 * 
 * @example
 * <TriageProjectPipeline 
 *   data={triageProjects} 
 *   isActive={true} 
 *   selectedYear="2023" 
 *   selectedQuarter="Q1/23" 
 * />
 */

import React, { useState, useEffect, useRef, useMemo } from 'react';
import './TriageProjectPipeline.css';
import { parseDate, removeTime, getQuarterFromDate, truncateText } from '../../utils/textUtils';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import { useTable, useSortBy, useGlobalFilter } from 'react-table';
import TriageProjectModal from '../../modals/TriageProjectModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortUp, faSortDown, faSort } from '@fortawesome/free-solid-svg-icons';
function TriageProjectPipeline({ data, isActive, selectedYear, selectedQuarter }) {



    const canvasRef = useRef(null);
    const tableRef = useRef(null);
    const [modals, setModals] = useState([]);
    
    // Drag to scroll state - COMPLETELY REBUILT
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, scrollLeft: 0 });
    const [lastMoveX, setLastMoveX] = useState(0);
    const [moveHistory, setMoveHistory] = useState([]);
    const tableContainerRef = useRef(null);
    const dragRedrawRef = useRef(null);
    const smoothScrollRef = useRef(0);
    const debounceTimeout = useRef(null);
    const lastRedrawTime = useRef(0);

    /**
     * Generates array of quarters within a date range
     * 
     * @param {Date} startDate - Start date of the range
     * @param {Date} endDate - End date of the range
     * @returns {Array<string>} Array of quarter strings in format 'Q1/23'
     */
    const generateQuartersInRange = (startDate, endDate) => {
        const quarters = [];
        let current = new Date(startDate);
        while (current <= endDate) {
            const year = current.getFullYear().toString().slice(-2);
            const quarter = getQuarterFromDate(current);
            if (quarter) {
                quarters.push(`${quarter}/${year}`);
            }
            current.setMonth(current.getMonth() + 3);
        }
        return quarters;
    };

    // Generate quarters centered on selected year/quarter with context
    const uniqueQuarters = useMemo(() => {
        // If specific quarter is selected, center on that quarter
        if (selectedQuarter && selectedQuarter !== '') {
            const [quarter, year] = selectedQuarter.split('/');
            const fullYear = `20${year}`;
            const centerDate = new Date(parseInt(fullYear), (parseInt(quarter.replace('Q', '')) - 1) * 3, 1);
            
            // Generate range: 2 years before to 2 years after the selected quarter
            const startDate = new Date(centerDate.getFullYear() - 2, 0, 1);
            const endDate = new Date(centerDate.getFullYear() + 2, 11, 31);
            return generateQuartersInRange(startDate, endDate);
        }
        
        // If specific year is selected (but no quarter), center on that year
        if (selectedYear && selectedYear !== 'All') {
            const targetYear = parseInt(selectedYear);
            const startDate = new Date(targetYear - 2, 0, 1);  // 2 years before
            const endDate = new Date(targetYear + 2, 11, 31);   // 2 years after
            return generateQuartersInRange(startDate, endDate);
        }
        
        // Fallback for "All Years": show comprehensive range from data + future context
        const allDates = data.flatMap(item => {
            const startDate = parseDate(item.dateItemAddedtoTriage);
            const endDate = parseDate(removeTime(item.closeDate));
            return startDate && endDate && !isNaN(startDate) && !isNaN(endDate)
                ? [startDate, endDate]
                : [];
        });
        if (!allDates.length) {
            // No data: show current year ±2 years
            const currentYear = new Date().getFullYear();
            const startDate = new Date(currentYear - 2, 0, 1);
            const endDate = new Date(currentYear + 2, 11, 31);
            return generateQuartersInRange(startDate, endDate);
        }
        
        const earliestDate = new Date(Math.min(...allDates));
        const latestDate = new Date(Math.max(...allDates));
        
        // Extend range: start from earliest data, but extend 1 year into future
        const extendedEndDate = new Date(Math.max(latestDate.getFullYear() + 1, new Date().getFullYear() + 1), 11, 31);
        return generateQuartersInRange(earliestDate, extendedEndDate);
    }, [data, selectedYear, selectedQuarter]);

    /**
     * Opens a modal for a specific project
     * 
     * @param {string|number} id - Unique identifier for the project
     * @param {Object} data - Project data object to display in modal
     */
    const handleShowModal = (id, data) => {
        setModals(prevModals => {
            const existingModalIndex = prevModals.findIndex(modal => modal.id === id);
            if (existingModalIndex !== -1) {
                const updatedModals = [...prevModals];
                const maxZIndex = Math.max(...prevModals.map(modal => modal.zIndex)) + 1;
                updatedModals[existingModalIndex].isOpen = true;
                updatedModals[existingModalIndex].zIndex = maxZIndex;
                return updatedModals;
            } else {
                const maxZIndex = Math.max(...prevModals.map(modal => modal.zIndex), 0) + 1;
                const newModal = { id, data, isOpen: true, top: `${15 + (prevModals.length * 2)}%`, left: `${20 + (prevModals.length * 2)}%`, zIndex: maxZIndex };
                return [...prevModals, newModal];
            }
        });
    };

    /**
     * Closes a modal by its ID
     * 
     * @param {string|number} id - Unique identifier for the modal to close
     */
    const handleCloseModal = id => {
        setModals(prevModals =>
            prevModals.map(modal => (modal.id === id ? { ...modal, isOpen: false } : modal))
        );
    };

    /**
     * Handles mouse down event for drag-to-scroll functionality
     * Prevents dragging on interactive elements and initializes drag state
     * 
     * @param {MouseEvent} e - Mouse event object
     */
    const handleMouseDown = (e) => {
        // Only start drag if clicking on table background, not on interactive elements
        const target = e.target;
        const isInteractiveElement = target.closest('.start-ball, .end-ball, .half-half-ball, a, button, .pointer');
        
        if (isInteractiveElement) return;
        
        const container = tableContainerRef.current;
        if (!container) return;
        
        // CRITICAL: Don't hijack scrollbar clicks!
        const containerRect = container.getBoundingClientRect();
        const scrollbarWidth = container.offsetWidth - container.clientWidth;
        const mouseX = e.clientX;
        const isScrollbarClick = mouseX > (containerRect.right - scrollbarWidth - 10); // Extra margin for safety
        
        if (isScrollbarClick) {
            return; // Let the scrollbar work normally
        }
        
        setIsDragging(true);
        setDragStart({
            x: e.pageX - container.offsetLeft,
            scrollLeft: container.scrollLeft
        });
        
        // Prevent text selection while dragging and add visual feedback
        e.preventDefault();
        document.body.style.userSelect = 'none';
        container.classList.add('dragging');
        container.style.cursor = 'grabbing';
    };

    /**
     * Handles mouse move event for drag-to-scroll functionality
     * Updates scroll position and tracks movement history for momentum scrolling
     * 
     * @param {MouseEvent} e - Mouse event object
     */
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const container = tableContainerRef.current;
        if (!container) return;
        
        e.preventDefault();
        
        const x = e.pageX - container.offsetLeft;
        const walk = (x - dragStart.x) * 1.2; // Reduced sensitivity
        const targetScroll = dragStart.scrollLeft - walk;
        
        // ULTIMATE ANTI-JITTER: Instant direct assignment with RAF
        if (smoothScrollRef.current) {
            cancelAnimationFrame(smoothScrollRef.current);
        }
        
        smoothScrollRef.current = requestAnimationFrame(() => {
            container.scrollLeft = targetScroll;
        });
        
        // Track movement for momentum calculation (always do this)
        const now = Date.now();
        setLastMoveX(x);
        setMoveHistory(prev => {
            const newHistory = [...prev.slice(-4), { x, time: now }]; // Keep last 5 moves
            return newHistory;
        });
        
        // NO MORE CANVAS REDRAWING DURING DRAG - This was causing the jitter!
        // Canvas will be redrawn only when drag ends
    };

    /**
     * Handles mouse up event to end drag-to-scroll and apply momentum scrolling
     * Cleans up drag state and applies smooth momentum animation
     */
    const handleMouseUp = () => {
        if (!isDragging) return;
        
        setIsDragging(false);
        document.body.style.userSelect = '';
        
        // Clean up any pending animation frames
        if (dragRedrawRef.current) {
            cancelAnimationFrame(dragRedrawRef.current);
            dragRedrawRef.current = null;
        }
        
        if (smoothScrollRef.current) {
            cancelAnimationFrame(smoothScrollRef.current);
            smoothScrollRef.current = null;
        }
        
        const container = tableContainerRef.current;
        if (container) {
            container.classList.remove('dragging');
            container.style.cursor = 'grab';
            
            // IMPORTANT: Redraw canvas after drag ends
            setTimeout(() => {
                updateCanvasSize();
                drawLines();
            }, 50); // Slightly longer delay for stability
            
            // Apply momentum scrolling
            if (moveHistory.length >= 2) {
                const recent = moveHistory.slice(-2);
                const timeDiff = recent[1].time - recent[0].time;
                const xDiff = recent[1].x - recent[0].x;
                
                if (timeDiff > 0) {
                    const velocity = (xDiff / timeDiff) * 16; // Adjust velocity
                    let momentum = velocity * 30; // Momentum distance
                    
                    if (Math.abs(momentum) > 5) { // Only apply if significant
                        const startScrollLeft = container.scrollLeft;
                        const targetScrollLeft = startScrollLeft - momentum;
                        
                        // Smooth momentum animation
                        const animateMomentum = () => {
                            const currentScrollLeft = container.scrollLeft;
                            const distance = targetScrollLeft - currentScrollLeft;
                            const step = distance * 0.1; // Ease out
                            
                            if (Math.abs(step) > 1) {
                                container.scrollLeft = currentScrollLeft + step;
                                requestAnimationFrame(animateMomentum);
                            }
                        };
                        
                        requestAnimationFrame(animateMomentum);
                    }
                }
            }
        }
        
        // Reset tracking
        setMoveHistory([]);
        setLastMoveX(0);
    };

    /**
     * Handles mouse leave event to end drag if active
     */
    const handleMouseLeave = () => {
        if (isDragging) {
            handleMouseUp();
        }
    };

    /**
     * Handles touch start event for mobile drag-to-scroll functionality
     * 
     * @param {TouchEvent} e - Touch event object
     */
    const handleTouchStart = (e) => {
        const target = e.target;
        const isInteractiveElement = target.closest('.start-ball, .end-ball, .half-half-ball, a, button, .pointer');
        
        if (isInteractiveElement) return;
        
        const container = tableContainerRef.current;
        if (!container) return;
        
        const touch = e.touches[0];
        setIsDragging(true);
        setDragStart({
            x: touch.pageX - container.offsetLeft,
            scrollLeft: container.scrollLeft
        });
        
        e.preventDefault();
        container.classList.add('dragging');
    };

    /**
     * Handles touch move event for mobile drag-to-scroll functionality
     * 
     * @param {TouchEvent} e - Touch event object
     */
    const handleTouchMove = (e) => {
        if (!isDragging) return;
        
        const container = tableContainerRef.current;
        if (!container) return;
        
        e.preventDefault();
        
        const touch = e.touches[0];
        const x = touch.pageX - container.offsetLeft;
        const walk = (x - dragStart.x) * 1.2; // Reduced sensitivity
        const targetScroll = dragStart.scrollLeft - walk;
        
        // ULTIMATE ANTI-JITTER: Instant direct assignment with RAF for touch
        if (smoothScrollRef.current) {
            cancelAnimationFrame(smoothScrollRef.current);
        }
        
        smoothScrollRef.current = requestAnimationFrame(() => {
            container.scrollLeft = targetScroll;
        });
        
        // Track movement for momentum calculation (always do this)
        const now = Date.now();
        setLastMoveX(x);
        setMoveHistory(prev => {
            const newHistory = [...prev.slice(-4), { x, time: now }];
            return newHistory;
        });
        
        // NO MORE CANVAS REDRAWING DURING TOUCH DRAG - This was causing the jitter!
        // Canvas will be redrawn only when drag ends
    };

    /**
     * Updates canvas size to match the table dimensions and positions it correctly
     * Clears the canvas when dimensions change
     */
    const updateCanvasSize = () => {
        const table = tableRef.current;
        const canvas = canvasRef.current;
        if (table && canvas) {
            const tableRect = table.getBoundingClientRect();
            const containerRect = table.parentNode.getBoundingClientRect();
            
            // Canvas will be cleared when width/height are set
            
            // Set canvas size to match the table exactly
            canvas.width = tableRect.width;
            canvas.height = tableRect.height;
            
            // Position canvas relative to the container, accounting for scroll
            const scrollTopOffset = table.parentNode.scrollTop;
            const scrollLeftOffset = table.parentNode.scrollLeft;
            canvas.style.top = `${tableRect.top - containerRect.top + scrollTopOffset}px`;
            canvas.style.left = `${tableRect.left - containerRect.left + scrollLeftOffset}px`;
            
            // Ensure canvas has the correct dimensions in style
            canvas.style.width = `${tableRect.width}px`;
            canvas.style.height = `${tableRect.height}px`;
            
            // Note: Canvas is automatically cleared when width/height are set
            // The calling code should immediately call drawLines() after this function
        }
    };

    /**
     * Draws connecting lines between project start and end dates on the canvas
     * Iterates through all rows and draws lines between start/end balls with highlighting
     */
    const drawLines = () => {
        const canvas = canvasRef.current;
        const table = tableRef.current;
        if (!canvas || !table) return;
        
        // Additional safety checks
        const tableBody = table.querySelector('tbody');
        if (!tableBody || !rows.length) return;
        
        const ctx = canvas.getContext('2d');
        // Use fillRect for faster clearing with alpha composite
        ctx.save();
        ctx.globalCompositeOperation = 'copy';
        ctx.fillStyle = 'rgba(0,0,0,0)';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.restore();
        
        // NUCLEAR Z-INDEX FIX: Clip canvas to exclude sticky column
        ctx.save();
        ctx.beginPath();
        ctx.rect(220, 0, canvas.width - 220, canvas.height);
        ctx.clip();
        
        const ballRadius = 10; // Updated to match bigger 20px balls
        const lineWidth = 3; // Slightly thinner lines too
        const tableContainer = table.parentNode;
        const containerRect = tableContainer.getBoundingClientRect();
        
        // Process each row to find start and end balls
        rows.forEach((row, rowIndex) => {
            const rowData = row.original;
            const startDate = parseDate(rowData.dateItemAddedtoTriage);
            const endDate = parseDate(removeTime(rowData.closeDate));
            
            if (!startDate || !endDate) return;
            
            const startQuarter = `${getQuarterFromDate(startDate)}/${startDate.getFullYear().toString().slice(-2)}`;
            const endQuarter = `${getQuarterFromDate(endDate)}/${endDate.getFullYear().toString().slice(-2)}`;
            
            // Find the column indices for start and end quarters
            const startColumnIndex = uniqueQuarters.indexOf(startQuarter);
            const endColumnIndex = uniqueQuarters.indexOf(endQuarter);
            
            // Only draw if both quarters are visible in our timeline
            if (startColumnIndex === -1 || endColumnIndex === -1) return;
            
            // Account for the 1 sticky column (Project Details only)
            const startCellColumnIndex = startColumnIndex + 1;
            const endCellColumnIndex = endColumnIndex + 1;
            
            // Find the actual DOM elements using more precise selectors
            const currentRow = tableBody.children[rowIndex];
            
            if (!currentRow) return;
            
            const startCell = currentRow.children[startCellColumnIndex];
            const endCell = currentRow.children[endCellColumnIndex];
            
            if (!startCell || !endCell) return;
            
            // Find the actual ball elements within the cells
            const startBall = startCell.querySelector('.start-ball, .half-half-ball');
            const endBall = endCell.querySelector('.end-ball, .half-half-ball');
            
            if (!startBall || !endBall) return;
            
            // Calculate positions relative to the canvas
            const startRect = startBall.getBoundingClientRect();
            const endRect = endBall.getBoundingClientRect();
            const tableRect = table.getBoundingClientRect();
            
            const fromX = startRect.left + startRect.width / 2 - tableRect.left;
            const fromY = startRect.top + startRect.height / 2 - tableRect.top;
            const toX = endRect.left + endRect.width / 2 - tableRect.left;
            const toY = endRect.top + endRect.height / 2 - tableRect.top;
            
            // Draw the line with enhanced visual feedback
            ctx.beginPath();
            drawLine(ctx, fromX, fromY, toX, toY, ballRadius);
            
            // Color the line based on project status or selection
            const isHighlighted = (selectedQuarter && (startQuarter === selectedQuarter || endQuarter === selectedQuarter)) ||
                                (selectedYear !== 'All' && (startQuarter.includes(`/${selectedYear.slice(-2)}`) || endQuarter.includes(`/${selectedYear.slice(-2)}`)));
            
            // SIMPLE CLEAN LINES - No background to worry about! 
            ctx.strokeStyle = isHighlighted ? '#56b32f' : '#2F7D32';
            ctx.lineWidth = isHighlighted ? lineWidth + 1 : lineWidth;
            ctx.stroke();
            
            // Add subtle shadow for depth
            if (isHighlighted) {
                ctx.beginPath();
                drawLine(ctx, fromX + 1, fromY + 1, toX + 1, toY + 1, ballRadius);
                ctx.strokeStyle = 'rgba(86, 179, 47, 0.3)';
                ctx.lineWidth = lineWidth + 2;
                ctx.stroke();
            }
        });
        
        // Restore clipping context
        ctx.restore();
    };

    /**
     * Draws a single line on the canvas context between two points
     * Adjusts line positioning to account for ball radius and visual offsets
     * 
     * @param {CanvasRenderingContext2D} ctx - Canvas 2D rendering context
     * @param {number} fromX - Starting X coordinate
     * @param {number} fromY - Starting Y coordinate
     * @param {number} toX - Ending X coordinate
     * @param {number} toY - Ending Y coordinate
     * @param {number} ballRadius - Radius of the connected balls
     * @param {number} [lineOffset=4] - Additional offset for line positioning
     */
    const drawLine = (ctx, fromX, fromY, toX, toY, ballRadius, lineOffset = 4) => {
        const adjustedFromX = fromX + ballRadius / 2 + lineOffset - 0.5; // PERFECT middle ground!
        const adjustedToX = toX - ballRadius / 2 + 1; // Still longer but not too far right!
        const adjustedFromY = fromY - 1; // Keep the perfect height!
        const adjustedToY = toY - 1; // Keep the perfect height!
        ctx.moveTo(adjustedFromX, adjustedFromY);
        ctx.lineTo(adjustedToX, adjustedToY);
    };

    // Columns setup with balls based on unique quarters
    const columns = useMemo(() => {
        return [
            {
                Header: () => <div style={{
                    fontSize: '0.8rem',
                    marginTop: '4px',
                    fontWeight: '600',
                    color: '#374151'
                }}>Project Details</div>,
                accessor: 'name',
                disableSortBy: true,
                Cell: ({ value, row }) => {
                    const description = row.original.childName || '';
                    const fullTooltip = `${value}\n\n${description || 'No description available'}`;
                    
                    return (
                        <Tippy content={<div style={{ maxWidth: '300px', whiteSpace: 'pre-wrap' }}>{fullTooltip}</div>}>
                            <div className="project-name-container" onClick={e => {
                                e.stopPropagation();
                                handleShowModal(row.original.id, row.original);
                            }}>
                                <div className="project-name">
                                    {truncateText(value, 25)}
                                </div>
                                {description && (
                                    <div className="project-description-preview">
                                        {truncateText(description, 45)}
                                    </div>
                                )}
                            </div>
                        </Tippy>
                    );
                }
            },
            ...uniqueQuarters.map(quarter => {
                // Check if this quarter is the selected one for highlighting
                const isSelectedQuarter = selectedQuarter === quarter;
                const isSelectedYear = selectedYear !== 'All' && quarter.includes(`/${selectedYear.slice(-2)}`);
                
                return {
                id: quarter, // Add explicit ID for react-table
                Header: () => (
                    <div 
                        className={`quarter-header ${isSelectedQuarter ? 'selected-quarter' : ''} ${isSelectedYear && !selectedQuarter ? 'selected-year' : ''}`}
                        style={{
                            fontSize: '0.75rem',
                            fontWeight: isSelectedQuarter ? '700' : '600',
                            color: isSelectedQuarter ? '#56b32f' : '#374151',
                            backgroundColor: isSelectedQuarter ? 'rgba(86, 179, 47, 0.1)' : 'transparent',
                            padding: '0.15rem',
                            borderRadius: '0.25rem',
                            border: isSelectedQuarter ? '1px solid #56b32f' : 'none',
                            lineHeight: '1.2'
                        }}
                    >
                        {quarter}
                    </div>
                ),
                accessor: row => {
                    const startDate = parseDate(row.dateItemAddedtoTriage);
                    const endDate = parseDate(removeTime(row.closeDate));
                    const startQuarter = startDate ? `${getQuarterFromDate(startDate)}/${startDate.getFullYear().toString().slice(-2)}` : null;
                    const endQuarter = endDate ? `${getQuarterFromDate(endDate)}/${endDate.getFullYear().toString().slice(-2)}` : null;

                    if (startQuarter === quarter && endQuarter === quarter) {
                        return (
                            <div className="ball-container">
                                <Tippy content={`${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`}>
                                    <div className="half-half-ball" style={{
                                        width: '20px',
                                        height: '20px',
                                        cursor: 'pointer'
                                    }}></div>
                                </Tippy>
                            </div>
                        );
                    } else if (startQuarter === quarter) {
                        return (
                            <Tippy content={`Start: ${startDate.toLocaleDateString()}`}>
                                <div className="start-ball" style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer'
                                }}></div>
                            </Tippy>
                        );
                    } else if (endQuarter === quarter) {
                        return (
                            <Tippy content={`End: ${endDate.toLocaleDateString()}`}>
                                <div className="end-ball" style={{
                                    width: '20px',
                                    height: '20px',
                                    cursor: 'pointer'
                                }}></div>
                            </Tippy>
                        );
                    } else {
                        return null;
                    }
                },
                disableSortBy: true,
                HeaderProps: { 
                    style: { 
                        textAlign: 'center',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: '#374151'
                    } 
                },
                CellProps: { style: { textAlign: 'center' } }
                };
            })
        ];
    }, [uniqueQuarters, data, selectedYear, selectedQuarter]);

    // Sort data by ranking before passing to table - SIMPLE!
    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => {
            const rankA = a.ranking;
            const rankB = b.ranking;
            
            // Handle empty/null values - put them at the end
            if (!rankA && !rankB) return 0;
            if (!rankA) return 1;
            if (!rankB) return -1;
            
            // Normal numeric comparison
            return rankA - rankB;
        });
    }, [data]);

    // Table setup - clean and simple!
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable(
        { columns, data: sortedData }, 
        useGlobalFilter, 
        useSortBy
    );

    // NO MORE AUTO-SCROLLING - Simple and clean! 🎯

    // CLEAN START - No auto-scrolling! Timeline starts at position 0 🎯

    // Effects for canvas adjustments with proper timing
    useEffect(() => { 
        if (!isActive || !data.length) return;
        
        updateCanvasSize(); 
        
        // Use requestAnimationFrame for better timing
        requestAnimationFrame(() => {
            // Additional delay for complex renders
            setTimeout(() => {
                updateCanvasSize(); // Update again to ensure accuracy
                drawLines();
            }, 150);
        });
    }, [data, rows, isActive, uniqueQuarters, selectedYear, selectedQuarter]);

    // Separate effect for initial mount and re-renders
    useEffect(() => {
        if (!isActive || !data.length) return;
        
        // Longer delay for initial render after refresh
        const timeoutId = setTimeout(() => {
            updateCanvasSize();
            drawLines();
            
            // NO MORE CONFLICTING SCROLL CODE - Let the dedicated 2023 effect handle it!
        }, 300);
        
        return () => clearTimeout(timeoutId);
    }, [isActive]); // Simplified dependencies - no interference from filter changes

    // Final fallback for stubborn refresh cases - CANVAS ONLY (no scroll interference!)
    useEffect(() => {
        if (!isActive || !data.length || !rows.length) return;
        
        // Multiple attempts with increasing delays - ONLY for canvas, NO SCROLLING
        const timeouts = [
            setTimeout(() => { updateCanvasSize(); drawLines(); }, 700),  // After 2023 scroll
            setTimeout(() => { updateCanvasSize(); drawLines(); }, 1000),
            setTimeout(() => { updateCanvasSize(); drawLines(); }, 1500)
        ];
        
        return () => timeouts.forEach(clearTimeout);
    }, [rows.length, isActive, data.length]);
    
    useEffect(() => {
        let resizeTimeout;
        let resizeObserver;
        let animationFrame;
        
        const forceRedraw = () => {
            // Cancel any pending animation frame
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
            
            // Use requestAnimationFrame for better timing
            animationFrame = requestAnimationFrame(() => {
            updateCanvasSize();
            drawLines();
            
                // Double-check with another frame to handle zoom/resize edge cases
                animationFrame = requestAnimationFrame(() => {
                updateCanvasSize();
                drawLines();
                });
            });
        };
        
        const handleResize = () => { 
            // Clear any existing timeout to debounce rapid resize events
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            
            // Immediate redraw attempt
            forceRedraw();
            
            // Debounced fallback with multiple attempts
            resizeTimeout = setTimeout(() => {
                forceRedraw();
                
                // Additional attempts for stubborn cases (zoom, etc.)
                setTimeout(() => forceRedraw(), 100);
                setTimeout(() => forceRedraw(), 300);
                setTimeout(() => forceRedraw(), 500);
            }, 150);
        };
        
        const handleScroll = () => { 
            forceRedraw();
        };
        
        // Enhanced zoom detection
        const handleVisibilityChange = () => {
            if (!document.hidden) {
                // Page became visible again, redraw lines
                setTimeout(() => forceRedraw(), 100);
            }
        };
        
        const tableContainer = tableContainerRef.current;
        const table = tableRef.current;
        
        // Set up ResizeObserver to watch the table directly
        if (table && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver((entries) => {
                // Clear existing timeout
                if (resizeTimeout) {
                    clearTimeout(resizeTimeout);
                }
                
                // Immediate attempt
                forceRedraw();
                
                // Debounced fallback
                resizeTimeout = setTimeout(() => {
                    forceRedraw();
                    
                    // Extra attempts for ResizeObserver edge cases
                    setTimeout(() => forceRedraw(), 50);
                    setTimeout(() => forceRedraw(), 150);
                }, 100);
            });
            
            resizeObserver.observe(table);
            // Also observe the container for better zoom detection
            if (tableContainer) {
                resizeObserver.observe(tableContainer);
            }
        }
        
        // Add zoom detection
        let lastDevicePixelRatio = window.devicePixelRatio;
        const checkZoom = () => {
            if (window.devicePixelRatio !== lastDevicePixelRatio) {
                lastDevicePixelRatio = window.devicePixelRatio;
                forceRedraw();
            }
        };
        
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        window.addEventListener('load', handleResize);
        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        // Check for zoom changes periodically
        const zoomCheckInterval = setInterval(checkZoom, 100);
        
        if (tableContainer) {
            tableContainer.addEventListener('scroll', handleScroll);
        }
        
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
            window.removeEventListener('load', handleResize);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            
            if (tableContainer) {
                tableContainer.removeEventListener('scroll', handleScroll);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            }
            if (resizeTimeout) {
                clearTimeout(resizeTimeout);
            }
            if (animationFrame) {
                cancelAnimationFrame(animationFrame);
            }
            if (zoomCheckInterval) {
                clearInterval(zoomCheckInterval);
            }
        };
    }, []);

    // Mouse drag event listeners
    useEffect(() => {
        const container = tableContainerRef.current;
        if (!container) return;
        
        // Set initial cursor style
        container.style.cursor = 'grab';
        
        // Add mouse event listeners
        container.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        container.addEventListener('mouseleave', handleMouseLeave);
        
        // Add touch event listeners for mobile support
        container.addEventListener('touchstart', handleTouchStart, { passive: false });
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleMouseUp);
        
        return () => {
            container.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            container.removeEventListener('mouseleave', handleMouseLeave);
            
            container.removeEventListener('touchstart', handleTouchStart);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleMouseUp);
            
            // Clean up styles
            document.body.style.userSelect = '';
            if (container) {
                container.style.cursor = '';
            }
        };
    }, [isDragging, dragStart]);

    return (
        <>
            {modals.map(modal => modal.isOpen && (
                <TriageProjectModal key={modal.id} data={modal.data} show={modal.isOpen} onClose={() => handleCloseModal(modal.id)}
                    style={{ top: modal.top, left: modal.left, zIndex: modal.zIndex }} allowMove />
            ))}
            <div className="project-timeline">
                <div 
                    ref={tableContainerRef}
                    className="table-container sticky-table-container" 
                    style={{ position: 'relative' }}
                >
                    <canvas ref={canvasRef} style={{ position: 'absolute', zIndex: 1 }}></canvas>
                    <table {...getTableProps()} className="custom-timeline-table" ref={tableRef} style={{ width: 'max-content', minWidth: `${Math.max(600, uniqueQuarters.length * 70 + 220)}px` }}>
                        <thead>
                            {headerGroups.map((headerGroup, headerIndex) => {
                                const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
                                return (
                                    <tr key={key || headerIndex} {...restHeaderGroupProps}>
                                        {headerGroup.headers.map((column, colIndex) => {
                                            const { key: columnKey, ...restColumnProps } = column.getHeaderProps(
                                                column.disableSortBy ? {} : column.getSortByToggleProps()
                                            );
                                            return (
                                                <th
                                                    key={columnKey || colIndex}
                                                    {...restColumnProps}
                                                    style={{
                                                        width: `${column.width || 'auto'}`,
                                                        textAlign: colIndex > 0 ? 'center' : 'left',
                                                        padding: '2px',
                                                        paddingLeft: colIndex === 0 ? '10px' : '4px',
                                                        cursor: column.disableSortBy ? 'default' : 'pointer',
                                                        ...(colIndex > 0 && {
                                                            backgroundColor: '#f8fafc',
                                                            background: '#f8fafc'
                                                        })
                                                    }}
                                                   
                                                >
                                                    <div
                                                        style={{
                                                            display: 'flex',
                                                            justifyContent: colIndex > 0 ? 'center' : 'space-between',
                                                            alignItems: 'center',
                                                            width: '100%',
                                                            paddingRight: '2px'
                                                        }}
                                                    >
                                                        {column.render('Header')}
                                                        {colIndex === 0 && !column.disableSortBy && (
                                                            <span style={{ textAlign: 'center' }}>
                                                                {column.isSorted ? (
                                                                    column.isSortedDesc ? (
                                                                        <FontAwesomeIcon icon={faSortDown} />
                                                                    ) : (
                                                                        <FontAwesomeIcon icon={faSortUp} />
                                                                    )
                                                                ) : (
                                                                    <FontAwesomeIcon icon={faSort} />
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </thead>

                        <tbody {...getTableBodyProps()}>{rows.map(row => {
                            prepareRow(row);
                            const { key: rowKey, ...restRowProps } = row.getRowProps();
                            return (
                                <tr key={rowKey} {...restRowProps}>{row.cells.map(cell => {
                                    const { key: cellKey, ...restCellProps } = cell.getCellProps();
                                    return (
                                        <td key={cellKey} {...restCellProps}>{cell.render('Cell')}</td>
                                    );
                                })}</tr>
                            );
                        })}</tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

export default TriageProjectPipeline;
